import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import StarRating from './StarRating';
import StarRatingInput from './StarRatingInput';
import './CourseReviewDetail.css';
import { AppHeader, BottomNavigation, CreateOptionsMenu, ConfirmationModal , BadWordsModal, ShareModal, BackButton, Button } from './shared';
import {
  popoverButtonContainerStyle,
  getCancelButtonStyle,
  getSubmitButtonStyle
} from './shared/PopoverStyles';
import { useLogout } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
// Issue #101: Removed useGlobalModal - using NavigationContext for create pages
import { useLanguage } from '../contexts/LanguageContext';
import { useAds } from '../contexts/AdContext'; // Issue #311: Ad initialization
import { AdBanner } from './ads'; // Issue #311: Banner ads

function CourseReviewDetail({ course }) {
  const navigation = useNavigation();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  // Issue #101: Removed openMarketplaceModal - using NavigationContext for create pages
  const { t } = useLanguage();
  const { initFromUserData } = useAds(); // Issue #311: Initialize ads

  const [userData, setUserData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [courseData, setCourseData] = useState(course); // Local state for updated rating/count

  // Issue #39: Create review states
  const [isCreateReviewOpen, setIsCreateReviewOpen] = useState(false);
  const [newRating, setNewRating] = useState(0);
  const [newReviewText, setNewReviewText] = useState('');
  const [createError, setCreateError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Issue #43: Edit/Delete review states
  const [openReviewMenuId, setOpenReviewMenuId] = useState(null);
  const [isEditReviewOpen, setIsEditReviewOpen] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [editRating, setEditRating] = useState(0);
  const [editReviewText, setEditReviewText] = useState('');
  const [editError, setEditError] = useState('');
  // Issue #161: State for bad words modal (reviews)
  const [showReviewBadWordsModal, setShowReviewBadWordsModal] = useState(false);
  const [reviewBadWords, setReviewBadWords] = useState([]);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [deletingReviewId, setDeletingReviewId] = useState(null);
  const [isDeletingReview, setIsDeletingReview] = useState(false);

  // Issue #73: Create options state for bottom navigation
  const [showCreateOptions, setShowCreateOptions] = useState(false);

  // Issue #312: Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);

  // Issue #319: Course menu state for 3-dot menu
  const [openCourseMenu, setOpenCourseMenu] = useState(false);

  // Issue #311: Show banner ad every 3rd course review page visit
  const STORAGE_KEY = 'courseReviewDetailVisitCount';
  const counterIncrementedRef = useRef(false);

  // Read current count and determine if ad should show (no side effects in useState)
  const [shouldShowAd] = useState(() => {
    const currentCount = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    const nextCount = currentCount + 1;
    return nextCount % 3 === 0;
  });

  const [adReady, setAdReady] = useState(false);

  // Increment counter and set timer (ref guard prevents double-increment in StrictMode)
  useEffect(() => {
    if (!counterIncrementedRef.current) {
      counterIncrementedRef.current = true;
      const currentCount = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
      localStorage.setItem(STORAGE_KEY, (currentCount + 1).toString());
    }

    if (!shouldShowAd) return;

    const timer = setTimeout(() => setAdReady(true), 300);
    return () => clearTimeout(timer);
  }, [shouldShowAd]);

  // Issue #72: Check if current user has already reviewed this course
  const hasUserReviewed = userData && reviews.some(review => review.user_id === userData.id);

  const fetchReviews = async () => {
    try {
      if (!course?.id) return;

      // Issue #39: Fetch actual course reviews from backend
      const reviewsResponse = await axios.get(`/api/courses/${course.id}/reviews`);
      const fetchedReviews = reviewsResponse.data || [];
      setReviews(fetchedReviews);

      // Update course rating and count from fetched reviews
      if (fetchedReviews.length > 0) {
        const totalRating = fetchedReviews.reduce((sum, r) => sum + r.rating, 0);
        const avgRating = totalRating / fetchedReviews.length;
        setCourseData(prev => ({
          ...prev,
          average_rating: avgRating,
          review_count: fetchedReviews.length
        }));
      } else {
        setCourseData(prev => ({
          ...prev,
          average_rating: 0,
          review_count: 0
        }));
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setReviews([]);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Get user info to display university name
        const userResponse = await axios.get('/api/user/me');
        setUserData(userResponse.data);
        // Issue #311: Initialize AdContext for banner ads
        initFromUserData(userResponse.data);

        // Fetch course reviews
        await fetchReviews();
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [course]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Issue #39: Open create review popover
  const handleOpenCreateReview = () => {
    setIsCreateReviewOpen(true);
    setNewRating(0);
    setNewReviewText('');
    setCreateError('');
  };

  // Issue #39: Close create review popover
  const handleCloseCreateReview = () => {
    setIsCreateReviewOpen(false);
    setNewRating(0);
    setNewReviewText('');
    setCreateError('');
  };

  // Issue #39: Submit new review
  const handleSubmitReview = async () => {
    // Issue #39 Scenario 1: User must select a rating (1-5 stars)
    if (newRating === 0) {
      setCreateError('Bitte gib eine Bewertung ab (1-5 Sterne)');
      return;
    }

    setIsSubmitting(true);
    setCreateError('');

    try {
      // Issue #39 Scenario 2: Submit review with optional text
      await axios.post(`/api/courses/${course.id}/reviews`, {
        rating: newRating,
        review_text: newReviewText.trim() || null
      });

      // Success - close popover and refresh data
      handleCloseCreateReview();

      // Issue #39 Scenario 3: Refresh reviews and course data to show updated rating
      await fetchReviews();

      // Notify parent to refresh course list (for updated average rating)
      // Navigation context is available via navigation.onNavigateToCourseReview if needed
    } catch (error) {
      // Issue #39 Scenario 2: Handle inappropriate content error
      // Issue #39 Scenario 4: Handle duplicate review error
      // Issue #161: Check for bad words in response
      if (error.response?.data?.bad_words && error.response.data.bad_words.length > 0) {
        setReviewBadWords(error.response.data.bad_words);
        setShowReviewBadWordsModal(true);
      } else if (error.response?.data?.message) {
        setCreateError(error.response.data.message);
      } else {
        setCreateError('Fehler beim Erstellen der Bewertung. Bitte versuche es erneut.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Issue #43: Open edit review popover
  const handleOpenEditReview = (review) => {
    setEditingReview(review);
    setEditRating(review.rating);
    setEditReviewText(review.review_text || '');
    setEditError('');
    setIsEditReviewOpen(true);
    setOpenReviewMenuId(null);
  };

  // Issue #43: Close edit review popover
  const handleCloseEditReview = () => {
    setIsEditReviewOpen(false);
    setEditingReview(null);
    setEditRating(0);
    setEditReviewText('');
    setEditError('');
  };

  // Issue #43: Submit edited review
  const handleSubmitEditReview = async () => {
    if (editRating === 0) {
      setEditError('Bitte gib eine Bewertung ab (1-5 Sterne)');
      return;
    }

    setIsEditSubmitting(true);
    setEditError('');

    try {
      await axios.put(`/api/courses/${course.id}/reviews/${editingReview.id}`, {
        rating: editRating,
        review_text: editReviewText.trim() || null
      });

      handleCloseEditReview();
      await fetchReviews();
    } catch (error) {
      // Issue #161: Check for bad words in response
      if (error.response?.data?.bad_words && error.response.data.bad_words.length > 0) {
        setReviewBadWords(error.response.data.bad_words);
        setShowReviewBadWordsModal(true);
      } else if (error.response?.data?.message) {
        setEditError(error.response.data.message);
      } else {
        setEditError('Fehler beim Bearbeiten der Bewertung. Bitte versuche es erneut.');
      }
    } finally {
      setIsEditSubmitting(false);
    }
  };

  // Issue #43: Delete review - show confirmation modal
  const handleDeleteReview = (reviewId) => {
    setDeletingReviewId(reviewId);
    setOpenReviewMenuId(null);
  };

  // Issue #43: Cancel delete
  const handleCancelDelete = () => {
    setDeletingReviewId(null);
  };

  // Issue #43: Confirm delete
  const handleConfirmDelete = async () => {
    if (!deletingReviewId) return;

    setIsDeletingReview(true);
    try {
      await axios.delete(`/api/courses/${course.id}/reviews/${deletingReviewId}`);
      setDeletingReviewId(null);
      await fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      alert('Fehler beim Löschen der Bewertung. Bitte versuche es erneut.');
    } finally {
      setIsDeletingReview(false);
    }
  };

  return (
    <div className="crd-page">
      <div className="crd-container">
        {/* Header - Using shared AppHeader component */}
        <AppHeader
          userData={userData}
          onLogout={handleLogout}
          isLoggingOut={isLoggingOut}
          logoutError={logoutError}
          onClearLogoutError={clearLogoutError}
        >
          <div className="crd-title-section">
            <button className="crd-title">{t('courseReview.detailTitle')}</button>
          </div>
        </AppHeader>

        {/* Back Button - same design as MarketplaceDetailPage */}
        <div className="crd-back-button-container">
          <BackButton
            onClick={navigation.onNavigateBack}
            className="crd-back-button"
          />
        </div>

        {/* Course Info Section */}
        <div className="crd-course-info">
          <div className="crd-course-header">
            {/* Issue #319: Course name with 3-dot menu */}
            <div className="crd-course-title-row">
              <h2 className="crd-course-name">{course?.name}</h2>
              {/* Issue #319: 3-dot menu for sharing course review */}
              <div className="crd-course-menu">
                <button
                  className="crd-course-menu-button"
                  onClick={() => setOpenCourseMenu(!openCourseMenu)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setOpenCourseMenu(false);
                    } else if (e.key === 'ArrowDown' && openCourseMenu) {
                      e.preventDefault();
                      const menu = e.currentTarget.parentElement.querySelector('[role="menu"]');
                      const firstItem = menu?.querySelector('[role="menuitem"]');
                      firstItem?.focus();
                    }
                  }}
                  aria-haspopup="menu"
                  aria-expanded={openCourseMenu}
                  aria-label={t('share.shareCourse')}
                >
                  ⋮
                </button>
                {openCourseMenu && (
                  <>
                    <div
                      className="crd-course-menu-overlay"
                      onClick={() => setOpenCourseMenu(false)}
                    ></div>
                    <div className="crd-course-menu-dropdown" role="menu" aria-label={t('share.shareCourse')}>
                      <button
                        role="menuitem"
                        tabIndex={0}
                        onClick={() => {
                          setOpenCourseMenu(false);
                          setShareModalOpen(true);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setOpenCourseMenu(false);
                            e.currentTarget.closest('.crd-course-menu').querySelector('.crd-course-menu-button')?.focus();
                          }
                        }}
                      >
                        {t('share.shareCourse')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="crd-rating-section">
              <StarRating
                rating={courseData?.average_rating || 0}
                reviewCount={courseData?.review_count || 0}
                size="large"
              />
            </div>
          </div>

          {/* Create Review Button - Issue #39 */}
          {/* Issue #72: Only show button if user hasn't reviewed yet */}
          {/* Issue #135: Updated button design for consistency */}
          {/* Issue #319: Share button moved to 3-dot menu next to course name */}
          {!hasUserReviewed && (
            <div className="crd-create-review-section">
              <Button variant="primary" className="crd-create-review-button" onClick={handleOpenCreateReview}>
                <span className="crd-edit-icon" aria-hidden="true">&#128221;</span>
                <span className="crd-create-review-text">{t('courseReview.createReview')}</span>
              </Button>
            </div>
          )}
        </div>

        {/* Reviews List - moved outside crd-course-info for grey background */}
        <div className="crd-reviews-section">
          <h3 className="crd-reviews-header">Reviews ({reviews.length})</h3>

          {loading && (
            <div className="crd-loading">Bewertungen werden geladen...</div>
          )}

          {!loading && reviews.length === 0 && (
            <div className="crd-empty">Keine Bewertungen vorhanden</div>
          )}

          {!loading && reviews.map((review) => (
            <div key={review.id} className="crd-review-card">
              <div className="crd-review-header">
                <div className="crd-review-author-section">
                  <span className="crd-review-author">{review.author_name}</span>
                  <span className="crd-review-date">{formatDate(review.created_at)}</span>
                </div>

                {/* Issue #43: 3-dot menu for user's own review */}
                {/* Issue #115: Added keyboard accessibility */}
                {userData && review.user_id === userData.id && (
                  <div className="crd-review-menu">
                    <button
                      className="crd-review-menu-button"
                      onClick={() => setOpenReviewMenuId(openReviewMenuId === review.id ? null : review.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setOpenReviewMenuId(null);
                        } else if (e.key === 'ArrowDown' && openReviewMenuId === review.id) {
                          e.preventDefault();
                          const menu = e.currentTarget.parentElement.querySelector('[role="menu"]');
                          const firstItem = menu?.querySelector('[role="menuitem"]');
                          firstItem?.focus();
                        }
                      }}
                      aria-haspopup="menu"
                      aria-expanded={openReviewMenuId === review.id}
                      aria-label="Review-Optionen"
                    >
                      ⋮
                    </button>
                    {openReviewMenuId === review.id && (
                      <>
                        <div
                          className="crd-review-menu-overlay"
                          onClick={() => setOpenReviewMenuId(null)}
                        ></div>
                        <div className="crd-review-menu-dropdown" role="menu" aria-label="Review-Optionen">
                          <button
                            role="menuitem"
                            tabIndex={0}
                            onClick={() => handleOpenEditReview(review)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setOpenReviewMenuId(null);
                                e.currentTarget.closest('.crd-review-menu').querySelector('.crd-review-menu-button')?.focus();
                              } else if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                const next = e.currentTarget.nextElementSibling;
                                if (next?.getAttribute('role') === 'menuitem') next.focus();
                              } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                const prev = e.currentTarget.previousElementSibling;
                                if (prev?.getAttribute('role') === 'menuitem') prev.focus();
                              }
                            }}
                          >
                            Review bearbeiten
                          </button>
                          <button
                            role="menuitem"
                            tabIndex={0}
                            className="delete-option"
                            onClick={() => handleDeleteReview(review.id)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') {
                                setOpenReviewMenuId(null);
                                e.currentTarget.closest('.crd-review-menu').querySelector('.crd-review-menu-button')?.focus();
                              } else if (e.key === 'ArrowDown') {
                                e.preventDefault();
                                const next = e.currentTarget.nextElementSibling;
                                if (next?.getAttribute('role') === 'menuitem') next.focus();
                              } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                const prev = e.currentTarget.previousElementSibling;
                                if (prev?.getAttribute('role') === 'menuitem') prev.focus();
                              }
                            }}
                          >
                            Review löschen
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="crd-review-rating">
                <StarRating
                  rating={review.rating}
                  reviewCount={null}
                  size="small"
                />
              </div>

              <div className="crd-review-text-container">
                <p className="crd-review-text">{review.review_text}</p>
              </div>
            </div>
          ))}

          {/* Issue #311: Banner ad at end of course reviews (every 3rd visit) */}
          {!loading && adReady && (
            <AdBanner slot="course-review-detail-end" />
          )}
        </div>
      </div>

      {/* Issue #73: Bottom Navigation Bar - Using shared component */}
      <BottomNavigation
        activeTab="home"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Issue #73: Create Options Menu - Using shared component */}
      {/* Issue #101: Removed onCreateMarketplace prop to use NavigationContext */}
      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />

      {/* Issue #39: Create Review Popover */}
      {isCreateReviewOpen && (
        <div className="crd-review-popover-overlay" onClick={handleCloseCreateReview}>
          <div className="crd-review-popover" onClick={(e) => e.stopPropagation()}>
            <div className="crd-review-popover-header">
              <span className="crd-popover-edit-icon">✏️</span>
              <span className="crd-popover-title">{t('courseReview.createReview')}</span>
            </div>

            <div className="crd-review-popover-content">
              <p className="crd-review-instruction">
                {t('courseReview.rateWithStars')}
              </p>

              <StarRatingInput
                rating={newRating}
                onRatingChange={setNewRating}
              />

              <div className="crd-review-text-field">
                <span className="crd-text-field-icon">💬</span>
                <textarea
                  className="crd-review-textarea"
                  placeholder={t('courseReview.sharePlaceholder')}
                  value={newReviewText}
                  onChange={(e) => {
                    setNewReviewText(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  maxLength={1000}
                  style={{ overflow: 'hidden', resize: 'none', minHeight: '80px' }}
                />
              </div>

              {createError && (
                <div className="crd-review-error">{createError}</div>
              )}

              <div style={popoverButtonContainerStyle}>
                <button
                  onClick={handleCloseCreateReview}
                  disabled={isSubmitting}
                  style={getCancelButtonStyle(isSubmitting)}
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleSubmitReview}
                  disabled={isSubmitting || newRating === 0}
                  style={getSubmitButtonStyle(isSubmitting || newRating === 0)}
                >
                  {isSubmitting ? t('courseReview.creating') : t('courseReview.create')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Issue #43: Edit Review Popover */}
      {isEditReviewOpen && (
        <div className="crd-review-popover-overlay" onClick={handleCloseEditReview}>
          <div className="crd-review-popover" onClick={(e) => e.stopPropagation()}>
            <div className="crd-review-popover-header">
              <span className="crd-popover-edit-icon">✏️</span>
              <span className="crd-popover-title">{t('courseReview.editReview')}</span>
            </div>

            <div className="crd-review-popover-content">
              <p className="crd-review-instruction">
                {t('courseReview.rateWithStars')}
              </p>

              <StarRatingInput
                rating={editRating}
                onRatingChange={setEditRating}
              />

              <div className="crd-review-text-field">
                <span className="crd-text-field-icon">💬</span>
                <textarea
                  className="crd-review-textarea"
                  placeholder={t('courseReview.sharePlaceholder')}
                  value={editReviewText}
                  onChange={(e) => {
                    setEditReviewText(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  maxLength={1000}
                  style={{ overflow: 'hidden', resize: 'none', minHeight: '80px' }}
                />
              </div>

              {editError && (
                <div className="crd-review-error">{editError}</div>
              )}

              <div style={popoverButtonContainerStyle}>
                <button
                  onClick={handleCloseEditReview}
                  disabled={isEditSubmitting}
                  style={getCancelButtonStyle(isEditSubmitting)}
                >
                  {t('cancel')}
                </button>
                <button
                  onClick={handleSubmitEditReview}
                  disabled={isEditSubmitting || editRating === 0}
                  style={getSubmitButtonStyle(isEditSubmitting || editRating === 0)}
                >
                  {isEditSubmitting ? t('courseReview.saving') : t('save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Issue #43: Delete Confirmation Modal - using shared ConfirmationModal component */}
      <ConfirmationModal
        isOpen={!!deletingReviewId}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('courseReview.deleteReview')}
        message={t('courseReview.confirmDelete')}
        confirmText={t('delete')}
        confirmingText={t('courseReview.deleting')}
        isConfirming={isDeletingReview}
        variant="danger"
      />

      {/* Issue #161: Bad words modal for reviews */}
      <BadWordsModal
        isOpen={showReviewBadWordsModal}
        onClose={() => setShowReviewBadWordsModal(false)}
        badWords={reviewBadWords}
        contentType="review"
      />

      {/* Issue #312: Share modal for course reviews */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        shareUrl={`${window.location.origin}/share/course/${course?.id}`}
        shareText={t('share.courseMessage').replace('{name}', course?.name || '')}
        shareTitle={course?.name || 'UNIVERSE'}
        contentType="course"
      />

    </div>
  );
}

export default CourseReviewDetail;
